import React from "react";
import FormWrapper from "../shared/FormWrapper";
import ProgressBar from "../shared/ProgressBar";
import AutoSaveIndicator from "../shared/AutoSaveIndicator";

const refereeRoleOptions = [
  { value: "pastor", label: "Pastor" },
  { value: "elder", label: "Elder" },
  { value: "leader", label: "Leader" },
  { value: "deacon", label: "Deacon" },
  { value: "member", label: "Member" },
  { value: "other", label: "Other" },
];

export default function ChurchReference({
  formData,
  onInputChange,
  onContinue,
  onBack,
  onSaveAndExit,
  saving,
  lastSaved,
}) {
  const isValid =
    formData.refereeName?.trim() &&
    formData.refereeEmail?.trim() &&
    formData.refereePhone?.trim() &&
    formData.refereeRole;

  return (
    <FormWrapper>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 12px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#737373",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "color 0.2s ease",
          marginBottom: "8px",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#1a1a1a")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#737373")}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      {/* Progress Bar */}
      <ProgressBar currentStep={5} totalSteps={5} />

      {/* Icon and Title */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "32px",
          animation: "fadeInUp 0.5s ease",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            backgroundColor: "#eff6ff",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            border: "1px solid #dbeafe",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "8px",
            letterSpacing: "-0.5px",
          }}
        >
          Church reference
        </h1>
        <p
          style={{
            fontSize: "15px",
            color: "#737373",
            margin: 0,
            lineHeight: "1.5",
          }}
        >
          Provide a reference from your church community to verify your profile.
        </p>
      </div>

      {/* Info box */}
      <div
        style={{
          padding: "16px",
          backgroundColor: "#eff6ff",
          borderRadius: "8px",
          marginBottom: "24px",
          border: "1px solid #dbeafe",
          animation: "fadeInUp 0.5s ease 0.1s both",
        }}
      >
        <p style={{ fontSize: "13px", color: "#1e40af", margin: 0, lineHeight: "1.5" }}>
          We'll send your reference an email to confirm they know you and can vouch
          for the information you've provided. Your profile will become visible once
          they confirm.
        </p>
      </div>

      {/* Referee Name */}
      <div style={{ marginBottom: "16px", animation: "fadeInUp 0.5s ease 0.15s both" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Referee's full name <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          type="text"
          value={formData.refereeName || ""}
          onChange={(e) => onInputChange("refereeName", e.target.value)}
          placeholder="E.g., John Smith"
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            color: "#1a1a1a",
            backgroundColor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            outline: "none",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#1a1a1a";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e5e5";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Referee Email */}
      <div style={{ marginBottom: "16px", animation: "fadeInUp 0.5s ease 0.2s both" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Referee's email <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          type="email"
          value={formData.refereeEmail || ""}
          onChange={(e) => onInputChange("refereeEmail", e.target.value)}
          placeholder="referee@example.com"
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            color: "#1a1a1a",
            backgroundColor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            outline: "none",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#1a1a1a";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e5e5";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Referee Phone */}
      <div style={{ marginBottom: "16px", animation: "fadeInUp 0.5s ease 0.25s both" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Referee's phone number <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          type="tel"
          value={formData.refereePhone || ""}
          onChange={(e) => onInputChange("refereePhone", e.target.value)}
          placeholder="E.g., 07700 900000"
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            color: "#1a1a1a",
            backgroundColor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            outline: "none",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#1a1a1a";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e5e5";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Referee Role */}
      <div style={{ marginBottom: "32px", animation: "fadeInUp 0.5s ease 0.3s both" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Referee's role at church <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <select
          value={formData.refereeRole || ""}
          onChange={(e) => onInputChange("refereeRole", e.target.value)}
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            color: formData.refereeRole ? "#1a1a1a" : "#a3a3a3",
            backgroundColor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            outline: "none",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#1a1a1a";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e5e5";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <option value="">Select their role...</option>
          {refereeRoleOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p style={{ fontSize: "13px", color: "#a3a3a3", marginTop: "8px" }}>
          This helps us understand their position in the church community
        </p>
      </div>

      {/* Auto-save indicator */}
      <AutoSaveIndicator saving={saving} lastSaved={lastSaved} />

      {/* Continue Button */}
      <button
        onClick={onContinue}
        disabled={!isValid}
        style={{
          width: "100%",
          padding: "16px 24px",
          fontSize: "16px",
          fontWeight: "600",
          color: "#fff",
          backgroundColor: isValid ? "#1a1a1a" : "#d4d4d4",
          border: "none",
          borderRadius: "8px",
          cursor: isValid ? "pointer" : "not-allowed",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
        onMouseEnter={(e) => {
          if (isValid) {
            e.currentTarget.style.backgroundColor = "#333";
            e.currentTarget.style.transform = "translateY(-1px)";
          }
        }}
        onMouseLeave={(e) => {
          if (isValid) {
            e.currentTarget.style.backgroundColor = "#1a1a1a";
            e.currentTarget.style.transform = "translateY(0)";
          }
        }}
      >
        Submit Application
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>

      {/* Save & Exit */}
      <button
        onClick={onSaveAndExit}
        style={{
          width: "100%",
          padding: "12px",
          marginTop: "12px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#a3a3a3",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "color 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#737373")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#a3a3a3")}
      >
        Save & finish later
      </button>
    </FormWrapper>
  );
}
